import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Zap, Star, ShieldCheck, Crown, ArrowRight, Lock, RefreshCw } from 'lucide-react';
import { api } from '../services/apiClient';

export default function PricingPage() {
    const [isAnnual, setIsAnnual] = useState(true);
    const [loading, setLoading] = useState(false);
    const [user, setUser] = useState(null);
    const navigate = useNavigate();

    // Vérifie si l'utilisateur est connecté et récupère ses infos
    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            api.get('/auth/me').then(res => {
                setUser(res.data);
            }).catch(err => console.error("Erreur auth:", err));
        }
    }, []);

    const handleSubscribe = async () => {
        if (!user) {
            navigate('/login');
            return;
        }

        setLoading(true);
        try {
            const plan = isAnnual ? 'annual' : 'monthly';
            const response = await api.post('/stripe/create-checkout-session', {
                plan: plan,
                user_id: user.id
            });
            
            if (response.data && response.data.checkout_url) {
                // Redirection vers la page de paiement Stripe
                window.location.href = response.data.checkout_url;
            }
        } catch (error) {
            console.error("Erreur Stripe:", error);
            alert("Une erreur est survenue lors de la création de la session de paiement.");
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 pt-24 pb-20 font-sans">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                
                {/* En-tête de la page */}
                <div className="text-center max-w-3xl mx-auto mb-16 animate-in fade-in slide-in-from-bottom-4 duration-500">
                    <h1 className="text-4xl md:text-5xl font-black text-slate-900 mb-6 tracking-tight">
                        Passez au niveau supérieur avec <span className="text-indigo-600">KeyForIn PRO</span>
                    </h1>
                    <p className="text-lg text-slate-500 font-medium">
                        Débloquez la puissance quantamentale. Prenez de meilleures décisions d'investissement en accédant aux outils réservés aux professionnels.
                    </p>
                </div>

                {/* Le Toggle Mensuel / Annuel */}
                <div className="flex justify-center mb-16 animate-in fade-in duration-700">
                    <div className="bg-white p-1.5 rounded-full border border-slate-200 shadow-sm inline-flex relative">
                        <button 
                            onClick={() => setIsAnnual(false)} 
                            className={`relative z-10 px-8 py-3 rounded-full text-sm font-bold transition-all duration-300 ${!isAnnual ? 'text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
                        >
                            Mensuel
                        </button>
                        <button 
                            onClick={() => setIsAnnual(true)} 
                            className={`relative z-10 px-8 py-3 rounded-full text-sm font-bold transition-all duration-300 flex items-center gap-2 ${isAnnual ? 'text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
                        >
                            Annuel
                            <span className="bg-emerald-100 text-emerald-700 text-[10px] px-2 py-0.5 rounded-full font-black uppercase tracking-wider animate-pulse">
                                -20%
                            </span>
                        </button>
                        <div 
                            className="absolute top-1.5 bottom-1.5 w-[calc(50%-6px)] bg-slate-100 rounded-full transition-transform duration-300 ease-out z-0 border border-slate-200 shadow-sm"
                            style={{ transform: isAnnual ? 'translateX(100%)' : 'translateX(0)' }}
                        ></div>
                    </div>
                </div>

                {/* Les Cartes de Prix */}
                <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto items-center animate-in fade-in slide-in-from-bottom-8 duration-700 delay-100">
                    
                    {/* Carte GRATUITE */}
                    <div className="bg-white rounded-3xl p-8 border border-slate-200 shadow-sm flex flex-col h-full mt-4 md:mt-0">
                        <div className="mb-6">
                            <span className="text-slate-500 font-bold uppercase tracking-widest text-xs">Découverte</span>
                            <h2 className="text-3xl font-black text-slate-900 mt-2">Gratuit</h2>
                            <div className="mt-4 flex items-baseline text-slate-900">
                                <span className="text-5xl font-black tracking-tight">0 €</span>
                                <span className="text-slate-500 font-bold ml-1">/ mois</span>
                            </div>
                            <p className="text-slate-500 text-sm mt-4 font-medium">Idéal pour démarrer et synchroniser son premier portefeuille IBKR.</p>
                        </div>

                        <div className="flex-1 space-y-4 mb-8">
                            <div className="flex items-start gap-3"><CheckCircle2 className="text-slate-300 shrink-0" size={20} /><span className="text-slate-600 text-sm font-medium">Suivi de 1 portefeuille</span></div>
                            <div className="flex items-start gap-3"><CheckCircle2 className="text-slate-300 shrink-0" size={20} /><span className="text-slate-600 text-sm font-medium">Analyse Sectorielle basique</span></div>
                            <div className="flex items-start gap-3"><CheckCircle2 className="text-slate-300 shrink-0" size={20} /><span className="text-slate-600 text-sm font-medium">Calendrier de dividendes</span></div>
                            <div className="flex items-start gap-3"><CheckCircle2 className="text-slate-300 shrink-0" size={20} /><span className="text-slate-600 text-sm font-medium">Watchlist & Heatmaps</span></div>
                            
                            <div className="pt-4 space-y-3">
                                <div className="flex items-start gap-3 opacity-40"><Lock className="text-slate-400 shrink-0" size={18} /><span className="text-slate-500 text-sm font-medium">Comparateur d'actions (CQP)</span></div>
                                <div className="flex items-start gap-3 opacity-40"><Lock className="text-slate-400 shrink-0" size={18} /><span className="text-slate-500 text-sm font-medium">Screener & Juste Prix</span></div>
                                <div className="flex items-start gap-3 opacity-40"><Lock className="text-slate-400 shrink-0" size={18} /><span className="text-slate-500 text-sm font-medium">Radar des Initiés (SEC)</span></div>
                                <div className="flex items-start gap-3 opacity-40"><Lock className="text-slate-400 shrink-0" size={18} /><span className="text-slate-500 text-sm font-medium">Arène des Gurus</span></div>
                            </div>
                        </div>

                        <button className="w-full py-4 rounded-xl font-bold text-slate-600 bg-slate-100 border border-slate-200 cursor-default">
                            Votre plan actuel
                        </button>
                    </div>

                    {/* Carte PRO */}
                    <div className="bg-gradient-to-b from-indigo-900 to-slate-900 rounded-3xl p-1 border border-indigo-500/30 shadow-2xl relative transform md:-translate-y-4 mt-4 md:mt-0">
                        <div className="bg-slate-900 rounded-[22px] p-8 h-full flex flex-col relative overflow-hidden">
                            
                            <div className="absolute top-0 inset-x-0 flex justify-center z-20">
                                <span className="bg-gradient-to-r from-indigo-500 to-violet-500 text-white text-[10px] font-black uppercase tracking-widest py-1 px-4 rounded-b-lg shadow-md flex items-center gap-1.5">
                                    <Crown size={14} /> Recommandé
                                </span>
                            </div>

                            <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500 rounded-full blur-[80px] opacity-20 pointer-events-none"></div>
                            
                            <div className="mb-6 relative z-10 pt-4">
                                <span className="text-indigo-400 font-bold uppercase tracking-widest text-xs flex items-center gap-2"><Star size={14} /> Premium</span>
                                <h2 className="text-3xl font-black text-white mt-2">PRO</h2>
                                
                                <div className="mt-4">
                                    {isAnnual ? (
                                        <>
                                            <div className="flex items-baseline text-white">
                                                <span className="text-5xl font-black tracking-tight">12,40 €</span>
                                                <span className="text-slate-400 font-bold ml-1">/ mois</span>
                                            </div>
                                            <p className="text-indigo-300 text-xs mt-2 font-bold bg-indigo-500/10 inline-block px-2 py-1 rounded border border-indigo-500/20">
                                                Facturé 149 € par an (2 mois offerts)
                                            </p>
                                        </>
                                    ) : (
                                        <>
                                            <div className="flex items-baseline text-white">
                                                <span className="text-5xl font-black tracking-tight">14,99 €</span>
                                                <span className="text-slate-400 font-bold ml-1">/ mois</span>
                                            </div>
                                            <p className="text-slate-400 text-xs mt-2 font-medium">Facturé mensuellement, sans engagement.</p>
                                        </>
                                    )}
                                </div>
                                <p className="text-slate-400 text-sm mt-4 font-medium">L'arsenal complet pour gérer son patrimoine comme un gestionnaire de fonds.</p>
                            </div>

                            <div className="flex-1 space-y-4 mb-8 relative z-10">
                                <div className="flex items-start gap-3"><Zap className="text-indigo-400 shrink-0" size={20} /><span className="text-slate-200 text-sm font-medium">Portefeuilles illimités</span></div>
                                <div className="flex items-start gap-3"><Zap className="text-indigo-400 shrink-0" size={20} /><span className="text-slate-200 text-sm font-medium">Watchlist & Heatmaps Mondiales</span></div>
                                <div className="flex items-start gap-3"><Zap className="text-indigo-400 shrink-0" size={20} /><span className="text-slate-200 text-sm font-medium">Screener Avancé & Calcul du Juste Prix</span></div>
                                <div className="flex items-start gap-3"><Zap className="text-indigo-400 shrink-0" size={20} /><span className="text-slate-200 text-sm font-medium">Comparateur d'actions (Score CQP)</span></div>
                                <div className="flex items-start gap-3"><Zap className="text-indigo-400 shrink-0" size={20} /><span className="text-slate-200 text-sm font-medium">Radar des Initiés (Base SEC Live)</span></div>
                                <div className="flex items-start gap-3"><Zap className="text-indigo-400 shrink-0" size={20} /><span className="text-slate-200 text-sm font-medium">Moteur DCA & Allocation Tactique</span></div>
                                <div className="flex items-start gap-3"><Zap className="text-indigo-400 shrink-0" size={20} /><span className="text-slate-200 text-sm font-medium">Arène des Gurus (Portefeuilles VIP)</span></div>
                            </div>

                            <button 
                                onClick={handleSubscribe}
                                disabled={loading}
                                className="w-full py-4 rounded-xl font-black text-white bg-indigo-600 hover:bg-indigo-500 shadow-[0_0_20px_rgba(79,70,229,0.3)] transition-all flex items-center justify-center gap-2 relative z-10 group disabled:opacity-75 disabled:scale-100"
                            >
                                {loading ? <RefreshCw className="animate-spin" size={18} /> : <>Passer au niveau PRO <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" /></>}
                            </button>
                            <p className="text-center text-[10px] text-slate-500 mt-3 font-medium flex items-center justify-center gap-1">
                                <ShieldCheck size={12} /> Paiement sécurisé par Stripe
                            </p>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
}
